import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { decodeReviewed, diffFingerprint, encodeReviewed, reviewIdsForHunk, toggleReviewIds } from "./lib/review-state.js";
import { hashGithubPath, parseGithubPullRequest } from "./lib/github-links.js";
import { AppContext } from "./app-context.js";
import { Brand } from "./components/brand.js";
import { Landing } from "./components/landing.js";
import { Viewer } from "./components/viewer.js";
import { allowProgressPaint, analyzeDiff, loadAnalyzer } from "./lib/analyzer.js";
import { collectReviewIds, indexComparisons } from "./lib/view-model.js";
import type {
  Analysis,
  AnalysisResult,
  AppContextValue,
  CommentDrag,
  CommentSelection,
  ComparisonIndex,
  FileDiff,
  WasmAnalyzer,
} from "./types.js";

function storedReviews(key: string): Set<string> {
  try {
    return decodeReviewed(localStorage.getItem(`cowdiff:reviewed:${key}`));
  } catch {
    return new Set();
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function hashAnalysisPaths(files: FileDiff[]): Promise<Map<string, string>> {
  const paths = new Set(files.flatMap((file) => [file.oldPath, file.newPath]).filter(Boolean));
  return new Map(await Promise.all([...paths].map(async (path) => [path, await hashGithubPath(path)] as const)));
}

interface AnalysisSource {
  githubPathHashes: Map<string, string>;
  githubPullUrl: string | null;
}

const localAnalysisSource: AnalysisSource = {
  githubPathHashes: new Map(),
  githubPullUrl: null,
};

function emptyComparisonIndex(): ComparisonIndex {
  return {
    collapsedComparisonIds: new Set(),
    collapsedLineIds: new Set(),
    lineComparisons: new Map(),
    realLocations: new Map(),
    wordChanges: new Map(),
  };
}

export function App() {
  const [wasm, setWasm] = useState<WasmAnalyzer | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [errorLink, setErrorLink] = useState<string | null>(null);
  const [diffKey, setDiffKey] = useState<string | null>(null);
  const [githubPullUrl, setGithubPullUrl] = useState<string | null>(null);
  const [githubPathHashes, setGithubPathHashes] = useState<Map<string, string>>(new Map());
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());
  const [showReviewed, setShowReviewed] = useState(false);
  const [hideUnchangedMoves, setHideUnchangedMoves] = useState(false);
  const [commentSelection, setCommentSelection] = useState<CommentSelection | null>(null);
  const commentDrag = useRef<CommentDrag | null>(null);

  const comparisons = useMemo(
    () => analysis ? indexComparisons(analysis) : emptyComparisonIndex(),
    [analysis],
  );
  const reviewIds = useMemo(
    () => (analysis ? collectReviewIds(analysis, reviewIdsForHunk) : new Set<string>()),
    [analysis],
  );

  useEffect(() => {
    let current = true;
    loadAnalyzer()
      .then((loaded) => {
        if (!current) return;
        setWasm(loaded);
        document.body.classList.add("wasm-ready");
        if (new URLSearchParams(window.location.search).has("demo")) {
          loadDemo(loaded);
        }
      })
      .catch((loadError) => setError(`Could not load the Rust analyzer: ${errorMessage(loadError)}`));
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("busy", busy);
    document.body.classList.toggle("github-source", githubPullUrl != null);
  }, [busy, githubPullUrl]);

  useEffect(() => {
    const endDrag = () => {
      commentDrag.current = null;
    };
    const clearSelection = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCommentSelection(null);
    };
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    window.addEventListener("keydown", clearSelection);
    return () => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      window.removeEventListener("keydown", clearSelection);
    };
  }, []);

  function showAnalysis(result: AnalysisResult, diff: string, source: AnalysisSource) {
    if ("error" in result) throw new Error(result.error);
    const key = diffFingerprint(diff);
    setAnalysis(result);
    setDiffKey(key);
    setGithubPullUrl(source.githubPullUrl);
    setGithubPathHashes(source.githubPathHashes);
    setReviewed(storedReviews(key));
    setShowReviewed(false);
    setHideUnchangedMoves(false);
    setCommentSelection(null);
    requestAnimationFrame(() => window.scrollTo({ top: 0 }));
  }

  async function analyzeText(diff: string, engine: WasmAnalyzer | null = wasm) {
    setErrorLink(null);
    if (!diff.trim()) {
      setError("Paste a unified diff first.");
      return;
    }
    setError("");
    setBusy(true);
    setProgress("Analyzing diff…");
    try {
      await allowProgressPaint();
      showAnalysis(analyzeDiff(engine, diff), diff, localAnalysisSource);
    } catch (analysisError) {
      setError(errorMessage(analysisError));
    } finally {
      setBusy(false);
    }
  }

  async function loadDemo(engine: WasmAnalyzer | null = wasm) {
    setError("");
    setErrorLink(null);
    setBusy(true);
    setProgress("Loading demo diff…");
    try {
      const response = await fetch("./demo.diff");
      if (!response.ok) throw new Error("Could not load the demo diff.");
      const diff = await response.text();
      setProgress("Analyzing diff…");
      await allowProgressPaint();
      showAnalysis(analyzeDiff(engine, diff), diff, localAnalysisSource);
    } catch (demoError) {
      setError(errorMessage(demoError));
    } finally {
      setBusy(false);
    }
  }

  async function analyzeGithub(url: string) {
    setErrorLink(null);
    if (!url.trim()) {
      setError("Enter a public GitHub pull request URL.");
      return;
    }
    setError("");
    setBusy(true);
    setProgress("Fetching pull request diff…");
    try {
      await allowProgressPaint();
      const pullRequest = parseGithubPullRequest(url);
      const response = await fetch(pullRequest.apiUrl, {
        headers: { Accept: "application/vnd.github.v3.diff" },
      });
      if (!response.ok) {
        if (response.status === 406) {
          setErrorLink(pullRequest.diffUrl);
          throw new Error(
            "GitHub's API will not serve this oversized diff. Open the full .diff, save it, then load it with Choose file.",
          );
        }
        throw new Error(`GitHub returned HTTP ${response.status}. Private PRs require authentication.`);
      }
      const diff = await response.text();
      setProgress("Analyzing pull request diff…");
      await allowProgressPaint();
      const result = analyzeDiff(wasm, diff);
      if ("error" in result) throw new Error(result.error);
      setProgress("Computing GitHub line anchors locally…");
      const pathHashes = await hashAnalysisPaths(result.files);
      setProgress("Rendering pull request…");
      await allowProgressPaint();
      showAnalysis(result, diff, {
        githubPathHashes: pathHashes,
        githubPullUrl: pullRequest.webUrl,
      });
    } catch (githubError) {
      setError(errorMessage(githubError));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setAnalysis(null);
    setDiffKey(null);
    setGithubPullUrl(null);
    setGithubPathHashes(new Map());
    setReviewed(new Set());
    setShowReviewed(false);
    setHideUnchangedMoves(false);
    setCommentSelection(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleReviewed(ids: string | string[]) {
    const values = Array.isArray(ids) ? ids : [ids];
    setReviewed((current) => {
      const next = toggleReviewIds(current, values);
      if (diffKey) {
        try {
          localStorage.setItem(`cowdiff:reviewed:${diffKey}`, encodeReviewed(next));
        } catch {
          // Review remains available for this session when storage is unavailable.
        }
      }
      return next;
    });
  }

  const context: AppContextValue = {
    analysis: analysis!,
    commentDrag,
    commentSelection,
    comparisons,
    githubPathHashes,
    githubPullUrl,
    hideUnchangedMoves,
    reviewed,
    reviewIds,
    setCommentSelection,
    setHideUnchangedMoves,
    setShowReviewed,
    showReviewed,
    toggleReviewed,
  };

  return (
    <AppContext.Provider value={context}>
      <Brand onReset={reset} />
      {busy && (
        <div class="progress-overlay" id="progress-overlay" role="status" aria-live="polite">
          <div class="progress-card">
            <div class="progress-heading">
              <span class="progress-spinner" aria-hidden="true" />
              <strong id="progress-label">{progress}</strong>
            </div>
            <div class="progress-track" aria-hidden="true"><i /></div>
          </div>
        </div>
      )}
      <main>
        {analysis ? (
          <Viewer onReset={reset} />
        ) : (
          <Landing
            busy={busy}
            error={error}
            errorLink={errorLink}
            onAnalyze={analyzeText}
            onDemo={() => loadDemo()}
            onGithub={analyzeGithub}
          />
        )}
      </main>
    </AppContext.Provider>
  );
}
